import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, startWith } from "rxjs";
import { AlertLabelComponent } from "@shared/components/molecules/alert-label/alert-label.component";

interface IInfoBannerConfig {
  start_date: string;
  end_date: string;
  resume_date: string;
  message: string;
}

@Component({
  selector: "app-info-banner",
  templateUrl: "./info-banner.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, AlertLabelComponent],
})
export class InfoBannerComponent {
  private readonly translateService = inject(TranslateService);

  private readonly bannerConfig$ = this.translateService.stream("info_banner").pipe(
    startWith(this.translateService.instant("info_banner")),
    map((config) => (config && typeof config === "object" ? (config as IInfoBannerConfig) : null)),
  );

  readonly bannerConfig = toSignal(this.bannerConfig$, { initialValue: null });

  readonly interpolationParams = computed(() => {
    const config = this.bannerConfig();
    if (!config) {
      return {};
    }
    const { start_date = "", end_date = "", resume_date = "" } = config;
    return {
      start_date: this.formatDate(this.parseDateString(start_date)),
      end_date: this.formatDate(this.parseDateString(end_date)),
      resume_date: this.formatDate(this.parseDateString(resume_date)),
    };
  });

  readonly isVisible = computed(() => {
    const config = this.bannerConfig();
    if (!config) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = this.parseDateString(config.start_date);
    startDate.setHours(0, 0, 0, 0);

    const endDate = this.parseDateString(config.end_date);
    endDate.setHours(23, 59, 59, 999);

    return today >= startDate && today <= endDate;
  });

  private parseDateString(dateString: string): Date {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
  }
}
